import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Eye, EyeOff, Sparkles, CheckCircle2, User, Mail, Lock, Gift, ShieldCheck } from 'lucide-react';
import { AuthSlider } from '@/components/AuthSlider';
import { SafeCryptoLogo } from '@/components/SafeCryptoLogo';
import { toast } from 'sonner';

export default function Register() {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [referralCode, setReferralCode] = useState(searchParams.get('ref') || '');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { register } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const hasReferral = referralCode.trim().length > 0;
  const totalBonus = hasReferral ? 225 : 200;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await register(email, password, name, referralCode);
      toast.success(t('register.registerSuccess'));
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || t('register.registerFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] flex">
      <AuthSlider />

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md space-y-6">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-2.5 mb-2">
            <SafeCryptoLogo size={32} />
            <span className="font-bold text-lg font-['Space_Grotesk']">Safe Crypto</span>
          </div>

          <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
            <CardHeader className="space-y-1 pb-4">
              <div className="flex items-center gap-3">
                <CardTitle className="text-2xl font-['Space_Grotesk']">{t('register.title')}</CardTitle>
              </div>
              <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('auth.createSecure')}</p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <Alert className="bg-[rgba(217,74,74,0.12)] border-[#D94A4A]/20">
                    <AlertDescription className="text-[#D94A4A]">{error}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="name">{t('register.name')}</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                    <Input
                      id="name"
                      data-testid="register-name-input"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder={t('register.namePlaceholder')}
                      className="bg-[hsl(var(--background))] border-[hsl(var(--border))] pl-10"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">{t('register.email')}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                    <Input
                      id="email"
                      type="email"
                      data-testid="auth-email-input"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      required
                      className="bg-[hsl(var(--background))] border-[hsl(var(--border))] pl-10"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">{t('register.password')}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      data-testid="auth-password-input"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={6}
                      placeholder={t('register.passwordPlaceholder')}
                      className="bg-[hsl(var(--background))] border-[hsl(var(--border))] pl-10 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="referral" className="flex items-center gap-2">
                    {t('register.referralCode')}
                    <span className="text-xs text-[hsl(var(--muted-foreground))]">{t('register.referralCodePlaceholder')}</span>
                    {hasReferral && (
                      <Badge className="bg-[rgba(45,211,111,0.12)] text-[#2DD36F] border-[#2DD36F]/20 text-xs">
                        <CheckCircle2 className="w-3 h-3 mr-1" /> +$25
                      </Badge>
                    )}
                  </Label>
                  <div className="relative">
                    <Gift className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                    <Input
                      id="referral"
                      data-testid="register-referral-input"
                      value={referralCode}
                      onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                      placeholder="ABCD1234"
                      className="bg-[hsl(var(--background))] border-[hsl(var(--border))] pl-10 uppercase tracking-wider"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  data-testid="auth-submit-button"
                  disabled={loading}
                  className="w-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary))]/90 h-11 font-semibold active:scale-[0.98] transition-all"
                >
                  {loading ? t('common.processing') : t('register.submit')}
                </Button>

                <p className="text-center text-sm text-[hsl(var(--muted-foreground))]">
                  {t('register.hasAccount')}{' '}
                  <Link to="/login" className="text-[hsl(var(--primary))] hover:underline font-medium">{t('register.loginLink')}</Link>
                </p>
              </form>
            </CardContent>
          </Card>

          {/* Trust footer */}
          <div className="flex items-center justify-center gap-4 text-[10px] text-[hsl(var(--muted-foreground))]/60">
            <span className="flex items-center gap-1"><Lock className="w-3 h-3" /> 256-bit SSL</span>
            <span className="flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> 2FA {t('auth.protected')}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
